import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { CloudUpload, TrashBinTrash } from "@papier/ui/components/icons";
import { Button } from "@papier/ui/components/ui/button";
import { Spinner } from "@papier/ui/components/ui/spinner";
import { cn } from "@papier/ui/lib/utils";

const defaultLabels = {
  upload: "Upload image",
  change: "Change image",
  remove: "Remove",
  instructions: "Square images work best. Changes are saved immediately.",
  uploading: "Uploading image…",
  removing: "Removing image…",
  updated: "Image updated.",
  removed: "Image removed.",
  invalidFile: "Choose an image file.",
  error: "Couldn't update the image. Please try again.",
};

type PickerState =
  | { status: "idle" }
  | { status: "uploading" | "removing"; preview: ReactNode; hasImage: boolean }
  | { status: "success"; action: "upload" | "remove"; message: string }
  | { status: "error"; message: string };

export function SettingsImagePicker({
  title,
  description,
  preview,
  hasImage,
  onUpload,
  onRemove,
  disabled = false,
  isPending = false,
  readOnlyMessage,
  labels: customLabels,
  getErrorMessage,
  className,
}: {
  title: string;
  description: string;
  preview: ReactNode;
  hasImage: boolean;
  onUpload: (file: File) => Promise<void>;
  onRemove?: () => Promise<void>;
  disabled?: boolean;
  isPending?: boolean;
  readOnlyMessage?: string;
  labels?: Partial<typeof defaultLabels>;
  getErrorMessage?: (error: unknown) => string;
  className?: string;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const uploadButton = useRef<HTMLButtonElement>(null);
  const [state, setState] = useState<PickerState>({ status: "idle" });
  const labels = { ...defaultLabels, ...customLabels };
  const busy = state.status === "uploading" || state.status === "removing";
  const unavailable = disabled || isPending || busy;
  // Auth mutations may update their query cache optimistically. Show the saved
  // preview and actions until the request finishes, including on failed retries.
  const displayedImage =
    state.status === "uploading" || state.status === "removing" ? state : { preview, hasImage };

  useEffect(() => {
    if (state.status === "success" && state.action === "remove") {
      // The remove action disappears after success; keep keyboard focus in the picker.
      uploadButton.current?.focus();
    }
  }, [state]);

  const upload = async (file: File) => {
    if (unavailable) return;
    if (!file.type.startsWith("image/")) {
      setState({ status: "error", message: labels.invalidFile });
      return;
    }
    setState({ status: "uploading", preview, hasImage });
    try {
      await onUpload(file);
      setState({ status: "success", action: "upload", message: labels.updated });
    } catch (error) {
      setState({ status: "error", message: getErrorMessage?.(error) || labels.error });
    }
  };

  const remove = async () => {
    if (unavailable || !onRemove) return;
    setState({ status: "removing", preview, hasImage });
    try {
      await onRemove();
      setState({ status: "success", action: "remove", message: labels.removed });
    } catch (error) {
      setState({ status: "error", message: getErrorMessage?.(error) || labels.error });
    }
  };

  return (
    <div
      role="group"
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-instructions`}
      aria-busy={isPending || busy}
      data-slot="settings-image-picker"
      className={cn("grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-4 gap-y-3", className)}
    >
      <div aria-hidden="true" className="shrink-0 sm:row-span-3">
        {displayedImage.preview}
      </div>
      <div className="min-w-0 self-center">
        <h3 id={`${id}-title`} className="text-sm font-semibold">
          {title}
        </h3>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        disabled={unavailable}
        onChange={(event) => {
          const file = event.target.files?.item(0);
          event.target.value = "";
          if (file) void upload(file);
        }}
      />
      <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-1 sm:col-start-2">
        <Button
          ref={uploadButton}
          type="button"
          variant="outline"
          disabled={unavailable}
          onClick={() => input.current?.click()}
        >
          {state.status === "uploading" ? <Spinner /> : <CloudUpload aria-hidden="true" />}
          {displayedImage.hasImage ? labels.change : labels.upload}
        </Button>
        {displayedImage.hasImage && onRemove && (
          <Button
            type="button"
            variant="ghost"
            disabled={unavailable}
            onClick={() => void remove()}
          >
            {state.status === "removing" ? <Spinner /> : <TrashBinTrash aria-hidden="true" />}
            {labels.remove}
          </Button>
        )}
      </div>
      <div className="col-span-2 space-y-2 text-xs leading-relaxed sm:col-span-1 sm:col-start-2">
        <p id={`${id}-instructions`} className="text-muted-foreground">
          {disabled && !isPending && readOnlyMessage ? readOnlyMessage : labels.instructions}
        </p>
        <p role="status" className="empty:hidden">
          {state.status === "uploading"
            ? labels.uploading
            : state.status === "removing"
              ? labels.removing
              : state.status === "success"
                ? state.message
                : null}
        </p>
        {state.status === "error" && (
          <p role="alert" className="text-destructive">
            {state.message}
          </p>
        )}
      </div>
    </div>
  );
}
